import { useEffect, useRef, useState, useCallback } from "react";
import { gsap, prefersReducedMotion, revealWords, revealBlock } from "../lib/motion";
import { TESTIMONIALS } from "../data/site";
import blobOrange from "../assets/decor/blob-orange.webp";
import "./Testimonials.css";

const Arrow = ({ dir }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d={dir === "prev" ? "M19 12H5m0 0 6-6m-6 6 6 6" : "M5 12h14m0 0-6-6m6 6-6 6"}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function Testimonials() {
  const rootRef = useRef(null);
  const slideRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const drag = useRef({ x: 0, active: false });

  const go = useCallback((next, direction) => {
    setDir(direction);
    setIndex((i) => (next + TESTIMONIALS.length) % TESTIMONIALS.length);
  }, []);

  const prev = useCallback(() => go(index - 1, -1), [go, index]);
  const next = useCallback(() => go(index + 1, 1), [go, index]);

  /* ---- section reveal ---- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      revealWords(root.querySelector(".testimonials__title"), { trigger: root });
      revealBlock(root.querySelector(".testimonials__intro"), { delay: 0.2 });
      revealBlock(root.querySelector(".testimonials__stage"), { delay: 0.1, y: 40 });

      if (prefersReducedMotion()) {
        gsap.set(".testimonials__blob", { autoAlpha: 1, scale: 1 });
        return;
      }

      gsap.fromTo(
        ".testimonials__blob",
        { autoAlpha: 0, scale: 0.3 },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1,
          ease: "back.out(2)",
          scrollTrigger: { trigger: root, start: "top 80%", once: true },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  /* ---- slide change animation ---- */
  useEffect(() => {
    const node = slideRef.current;
    if (!node || prefersReducedMotion()) return undefined;

    const tl = gsap.timeline();
    tl.fromTo(
      node,
      { autoAlpha: 0, xPercent: dir * 8, scale: 0.98 },
      { autoAlpha: 1, xPercent: 0, scale: 1, duration: 0.65, ease: "expo.out" }
    ).fromTo(
      // Only the active slide's parts — the other three are in the DOM for
      // crawlers but stacked at opacity 0, so animating them is wasted work.
      node.querySelectorAll(".testimonials__item.is-active [data-slide-part]"),
      { autoAlpha: 0, y: 14 },
      { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.06, ease: "power3.out" },
      0.08
    );

    return () => tl.kill();
  }, [index, dir]);

  /* ---- keyboard ---- */
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  /* ---- pointer drag ---- */
  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, active: true };
  };

  const onPointerUp = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.x;
    drag.current.active = false;
    if (Math.abs(dx) > 55) (dx < 0 ? next : prev)();
  };

  return (
    <section className="testimonials section" id="testimonials" ref={rootRef}>
      <div className="container testimonials__grid">
        <div className="testimonials__copy">
          <img
            className="testimonials__blob"
            src={blobOrange}
            alt=""
            aria-hidden="true"
            width="65"
            height="65"
          />
          <h2 className="testimonials__title section-title">Customer Testimonials</h2>
          <p className="testimonials__intro lede">
            Hear from travelers who have experienced the beauty of Jordan through Private Stays:
          </p>
        </div>

        <div
          className="testimonials__stage"
          role="group"
          aria-roledescription="carousel"
          aria-label="Customer testimonials"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          data-cursor="drag"
        >
          <button type="button" className="testimonials__nav testimonials__nav--prev" onClick={prev} aria-label="Previous testimonial">
            <Arrow dir="prev" />
          </button>

          {/*
            All four testimonials stay in the DOM — the reference keeps its whole
            slider track mounted, and rendering only the active one would hide
            three quotes from crawlers and from anyone reading the page source.
            Inactive slides are stacked underneath at opacity 0 and marked
            aria-hidden, so assistive tech follows the controls instead.
          */}
          <p className="sr-only" aria-live="polite">
            {`Slide ${index + 1} of ${TESTIMONIALS.length}`}
          </p>

          <div className="testimonials__slide" ref={slideRef}>
            {TESTIMONIALS.map((item, i) => (
              <article
                key={item.id}
                className={`testimonials__item ${i === index ? "is-active" : ""}`}
                aria-hidden={i !== index}
              >
                {/* No decorative glyph: the reference's copy carries its own
                    quotation marks, and a second mark would double them up. */}
                <blockquote className="quote">
                  <p className="quote__text" data-slide-part>
                    {item.quote}
                  </p>
                </blockquote>
                <div className="testimonials__person">
                  <p className="testimonials__name" data-slide-part>
                    {item.name}
                  </p>
                  <p className="testimonials__role" data-slide-part>
                    {item.role}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <button type="button" className="testimonials__nav testimonials__nav--next" onClick={next} aria-label="Next testimonial">
            <Arrow dir="next" />
          </button>

          <ul className="testimonials__dots">
            {TESTIMONIALS.map((item, i) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={i === index ? "is-active" : ""}
                  onClick={() => go(i, i > index ? 1 : -1)}
                  aria-label={`Show testimonial from ${item.name}`}
                  aria-current={i === index}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
